/*
 * Tra cứu trung tâm bảo hành — <warranty-center-locator>
 *
 * Dữ liệu: bảng dán trong nội dung một Page, Liquid in vào <template data-source>
 * (đã theo ngôn ngữ đang xem). 5 cột: Tỉnh/TP cũ | Tỉnh sau sáp nhập | Tên | Địa chỉ | Hotline.
 * Tách 3 phần: đọc bảng (parseTable) → lọc (filterCenters) → hiển thị (render*).
 */
(() => {
  if (customElements.get('warranty-center-locator')) return;

  /* ---------- Dữ liệu ---------- */

  const text = (value) => (value == null ? '' : String(value).replace(/\s+/g, ' ').trim());

  // Các dòng trong một ô bảng (tách theo <p>, <div>, <li>, <br>) — bảng dán từ Word hay dùng <p>.
  function cellLines(cell) {
    const copy = cell.cloneNode(true);
    copy.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    copy.querySelectorAll('p, div, li').forEach((block) => block.append('\n'));
    return copy.textContent.split('\n').map(text).filter(Boolean);
  }

  // "0243 655 8788 0242 261 9999" -> 2 số: số mới bắt đầu khi số đang gom đủ 10 chữ số
  // và cụm kế tiếp bắt đầu bằng 0 hoặc là 1800/1900.
  function splitPhones(line) {
    const phones = [];
    let current = [];
    (line.match(/\d+/g) || []).forEach((group) => {
      const startsNew = group.startsWith('0') || group === '1800' || group === '1900';
      if (current.length && current.join('').length >= 10 && startsNew) {
        phones.push(current.join(' '));
        current = [];
      }
      current.push(group);
    });
    if (current.length) phones.push(current.join(' '));
    return phones;
  }

  // Dòng chỉ có số -> số điện thoại; dòng chữ (vd "*Phím 1073...") -> ghi chú của số ngay trên nó.
  function parsePhones(lines) {
    const phones = [];
    const notes = [];
    lines.forEach((line) => {
      if (/^[\d\s.+()-]+$/.test(line)) {
        splitPhones(line).forEach((number) => phones.push({ number, note: '' }));
      } else if (phones.length) {
        const last = phones[phones.length - 1];
        last.note = text(`${last.note} ${line.replace(/^\*/, '')}`);
      } else {
        notes.push(line.replace(/^\*/, ''));
      }
    });
    return { phones, phoneNote: text(notes.join(' ')) };
  }

  function parseTable(root) {
    const centers = [];
    root.querySelectorAll('tr').forEach((row) => {
      const cells = [...row.children].filter((cell) => /^T[DH]$/.test(cell.tagName)).map(cellLines);
      const hotline = (cells[4] || []).join(' ');
      // Bỏ dòng tiêu đề / dòng thiếu dữ liệu: không có tên hoặc cột hotline không có chữ số
      if (cells.length < 5 || !cells[2].length || !/\d/.test(hotline)) return;
      const center = {
        id: `center-${centers.length + 1}`,
        // "Đà Nẵng 2", "Bình Dương 2" là nhãn nội bộ: bỏ số thứ tự ở cuối
        provinceOld: text(cells[0].join(' ')).replace(/\s+\d+$/, ''),
        provinceNew: text(cells[1].join(' ')),
        name: text(cells[2].join(' ')),
        address: text(cells[3].join(' ')),
        ...parsePhones(cells[4]),
      };
      center.regionKey = normalize(center.provinceNew);
      center.haystack = normalize([center.provinceOld, center.provinceNew, center.name, center.address].join(' '));
      centers.push(center);
    });
    return centers;
  }

  /* ---------- Lọc ---------- */

  // Không phân biệt hoa/thường, có dấu/không dấu, "đ"/"d", khoảng trắng thừa.
  function normalize(value) {
    return text(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/đ/g, 'd')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const collator = new Intl.Collator(document.documentElement.lang || 'vi', { sensitivity: 'base' });

  function getRegions(centers) {
    const regions = new Map();
    centers.forEach((center) => {
      if (center.regionKey && !regions.has(center.regionKey)) regions.set(center.regionKey, center.provinceNew);
    });
    return [...regions].map(([key, label]) => ({ key, label })).sort((a, b) => collator.compare(a.label, b.label));
  }

  function filterCenters(centers, { region, keyword }) {
    if (region) return centers.filter((center) => center.regionKey === region);
    const words = normalize(keyword).split(' ').filter(Boolean);
    if (!words.length) return centers;
    return centers.filter((center) => words.every((word) => center.haystack.includes(word)));
  }

  /* ---------- Hiển thị ---------- */

  const el = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content != null) node.textContent = content;
    return node;
  };

  const COLUMNS = ['region', 'name', 'address', 'phone'];

  // labels: tiêu đề 4 cột (lấy từ <th>), previous: chữ trước tỉnh cũ
  function renderRow(center, { labels, previous }) {
    const row = el('tr', 'wcl__row');
    row.setAttribute('role', 'row');
    row.dataset.id = center.id;

    const cells = {};
    COLUMNS.forEach((key, index) => {
      const cell = el('td', `wcl__cell wcl__cell--${key}`);
      cell.setAttribute('role', 'cell');
      cell.dataset.label = labels[index];
      cells[key] = cell;
      row.appendChild(cell);
    });

    cells.region.appendChild(el('span', 'wcl__region', center.provinceNew));
    if (center.provinceOld && normalize(center.provinceOld) !== center.regionKey) {
      cells.region.appendChild(el('span', 'wcl__region-old', `${previous} ${center.provinceOld}`.trim()));
    }
    cells.name.appendChild(el('span', 'wcl__name', center.name));
    cells.address.textContent = center.address;

    const phones = el('ul', 'wcl__phones');
    center.phones.forEach(({ number, note }) => {
      const item = el('li');
      const digits = number.replace(/[^\d+]/g, '');
      if (digits) {
        const link = el('a', 'wcl__phone', number);
        link.href = `tel:${digits}`;
        item.appendChild(link);
      } else {
        item.textContent = number;
      }
      if (note) item.appendChild(el('span', 'wcl__phone-note', note));
      phones.appendChild(item);
    });
    cells.phone.appendChild(phones);
    if (center.phoneNote) cells.phone.appendChild(el('p', 'wcl__phone-note', center.phoneNote));

    return row;
  }

  class WarrantyCenterLocator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('form');
      this.regionSelect = this.querySelector('[data-region]');
      this.keywordInput = this.querySelector('[data-keyword]');
      this.clearButton = this.querySelector('[data-clear]');
      this.summary = this.querySelector('[data-summary]');
      this.table = this.querySelector('[data-table]');
      this.tbody = this.querySelector('[data-tbody]');
      this.emptyState = this.querySelector('[data-state="empty"]');
      this.query = { region: '', keyword: '' };
      // Chữ giao diện render sẵn từ Liquid (setting section → dịch được bằng Translate & Adapt)
      this.allRegionsText = this.regionSelect.options[0].text;
      this.rowText = {
        labels: [...this.table.querySelectorAll('th')].map((th) => th.textContent.trim()),
        previous: this.dataset.textPrevious || '',
      };

      // Chọn khu vực: lọc ngay và xóa từ khóa (hai cách tìm độc lập — "HOẶC").
      this.regionSelect.addEventListener('change', () => {
        this.keywordInput.value = '';
        this.apply({ region: this.regionSelect.value, keyword: '' });
      });

      // Từ khóa: chạy khi bấm "Tìm kiếm" hoặc Enter; đưa dropdown về "Tất cả khu vực".
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!this.centers.length) return;
        this.regionSelect.value = '';
        this.apply({ region: '', keyword: this.keywordInput.value });
      });

      this.clearButton.addEventListener('click', () => {
        this.regionSelect.value = '';
        this.keywordInput.value = '';
        this.apply({ region: '', keyword: '' });
        this.keywordInput.focus();
      });

      this.centers = parseTable(this.querySelector('[data-source]').content);
      this.renderRegions();
      this.apply(this.query);
    }

    renderRegions() {
      const current = this.regionSelect.value;
      const fragment = document.createDocumentFragment();
      fragment.appendChild(new Option(this.allRegionsText, ''));
      getRegions(this.centers).forEach(({ key, label }) => fragment.appendChild(new Option(label, key)));
      this.regionSelect.replaceChildren(fragment);
      this.regionSelect.value = current;
      this.regionSelect.disabled = false;
    }

    apply(query) {
      this.query = { region: query.region || '', keyword: text(query.keyword) };
      const matches = filterCenters(this.centers, this.query);
      const filtered = Boolean(this.query.region || this.query.keyword);

      this.tbody.replaceChildren(...matches.map((center) => renderRow(center, this.rowText)));
      this.clearButton.hidden = !filtered;
      this.showState(matches.length ? 'table' : 'empty');
      this.setSummary(this.describe(matches.length, filtered));
    }

    describe(count, filtered) {
      const { textAll, textRegion, textKeyword } = this.dataset;
      let template = textAll;
      if (filtered) template = this.query.region ? textRegion : textKeyword;
      return (template || '')
        .replaceAll('{count}', count)
        .replaceAll('{region}', this.regionSelect.selectedOptions[0]?.text || '')
        .replaceAll('{keyword}', this.query.keyword);
    }

    showState(name) {
      this.emptyState.hidden = name !== 'empty';
      this.table.hidden = name !== 'table';
    }

    setSummary(message) {
      this.summary.textContent = message;
    }
  }

  customElements.define('warranty-center-locator', WarrantyCenterLocator);
})();
